import { Link } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert } from '@fluxora/ui';

/**
 * `/resume/expired` and `/resume/busy` (E-CRM04) — where a "resume your
 * enrollment" link lands when it cannot resume.
 *
 * `expired` is THE one failure page for every unusable link: unknown, expired,
 * already used (a link works once), revoked (a newer one was sent, or the lead
 * signed) and malformed. Accounts answers one identical 404 for all of them and
 * the BFF adds no branch, so this variant has nothing to vary on — and must not
 * grow a `?reason=`, for the offer page's reason (`OfferUnavailablePage`):
 * whoever holds an aged link is not necessarily the lead.
 *
 * `busy` is NOT a verdict on the link (QA 2026-09-28 F4): the BFF's per-IP limit
 * refused the click before the link was even looked at, so the link is untouched
 * and the page says "try again shortly". It used to be a raw 429 JSON body in the
 * customer's browser.
 *
 * A `<div>`, not a `<main>` (QA F5): the storefront layout already provides the
 * one `main` landmark. `role="status"`: an expected outcome, not a fault.
 */
export type ResumeExpiredVariant = 'expired' | 'busy';

export function ResumeExpiredPage({
  variant = 'expired',
}: {
  readonly variant?: ResumeExpiredVariant;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-5 px-4 py-10">
      <h1 className="text-xl font-bold text-primary">{t('shop.resume.expired.heading')}</h1>

      <Alert variant="warning" title={t(`shop.resume.${variant}.title`)} role="status">
        <div className="flex flex-col gap-3">
          <span>{t(`shop.resume.${variant}.body`)}</span>
          {variant === 'expired' ? (
            <div>
              <Link to="/" className="text-sm font-medium text-primary underline">
                {t('shop.resume.expired.startAgain')}
              </Link>
            </div>
          ) : null}
        </div>
      </Alert>
    </div>
  );
}
